import { useContext, useEffect, useRef } from "react";
import { AuthContext } from "@/hooks/useAuth";
import { useSettings } from "@/contexts/SettingsContext";

// List of public routes where timeout doesn't apply
const PUBLIC_ROUTES = ["/login", "/reset-password", "/forgot-password"];

function getCurrentPathname(): string {
    if (typeof window === "undefined") return "/";
    return window.location.pathname || "/";
}

export function SessionTimeoutGuard({ children }: { children: React.ReactNode }) {
    const auth = useContext(AuthContext);
    const { security, loading } = useSettings();

    // Default to 60 minutes if not loaded yet, or strictly apply the security setting
    const timeoutMinutes = loading ? 60 : security.sessionTimeoutMinutes || 60;
    const timeoutMs = timeoutMinutes * 60 * 1000;

    // Ref to hold the timeout timer
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const resetTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        const isPublicRoute = PUBLIC_ROUTES.some((route) => getCurrentPathname().startsWith(route));

        // Only enforce if logged in and not on a public route
        if (auth?.user && !isPublicRoute) {
            timerRef.current = setTimeout(() => {
                console.log(
                    `[SessionTimeoutGuard] User inactive for ${timeoutMinutes} minutes. Logging out.`,
                );
                void auth.logout();
            }, timeoutMs);
        }
    };

    useEffect(() => {
        // Setup initial timer
        resetTimer();

        // Listen for user activity
        const handleActivity = () => resetTimer();

        // Debounce activity listeners slightly to prevent excessive calls (browser handles these well, but good practice)
        window.addEventListener("mousemove", handleActivity, { passive: true });
        window.addEventListener("keydown", handleActivity, { passive: true });
        window.addEventListener("click", handleActivity, { passive: true });
        window.addEventListener("scroll", handleActivity, { passive: true });

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            window.removeEventListener("mousemove", handleActivity);
            window.removeEventListener("keydown", handleActivity);
            window.removeEventListener("click", handleActivity);
            window.removeEventListener("scroll", handleActivity);
        };
    }, [auth, timeoutMs]); // Re-run if auth state or timeout setting changes

    return <>{children}</>;
}
